// SingleCheckout.jsx
import { useEffect, useMemo, useState } from 'react';
import { redirect, useLocation, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from '../../backendClient';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Card } from 'react-bootstrap';
import SingleCheckoutForm from './SingleCheckoutForm';



// Fallback event used when API is missing or returns nothing
const FALLBACK_EVENT = {
    _id: null,
    name: 'Event Checkout',
    slug: 'dummy-event',
    currency: 'usd',
    tickets: [
      { _id: 'ga',  label: 'GA',  amount_cents: 3000 },
      { _id: 'vip', label: 'VIP', amount_cents: 7500 },
    ],
  };


const  SingleCheckout =  (props) => {
  const stripePromise = loadStripe('pk_live_51NtjiEID4DrgC8ZC2oAC6Jt3mAvjhzeQXr8rw19wLUW33J7eoVd9V7kvUBfqUeGoea7eJ0DkqNLsfAgQv6zDOUnb00AXQbueN4');
    // SingleCheckout.jsx
  const { locationState: locationStateProp, EventID: eventIdProp } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const incoming = location.state;

  const {currentUser} = useSelector(state => state.user);

    // Build a canonical redirectState if we have event context
//   const redirectState = 
//          {
//             source: 'event',
//             slug: incoming.slug,
//             eventId: incoming.eventId ?? null,
//             returnAfterLogin: `/single/${incoming.slug}`,
//             returnAfterRegister: `/single/${incoming.slug}`,
//             // event: incoming.event
//           };

    let redirectState = {}; 
        
    useEffect(() => {
            if (location.state) {
                redirectState = 
                {
                   source: 'event',
                   slug: incoming.slug,
                   eventId: incoming.eventId ?? null,
                   returnAfterLogin: `/single/${incoming.slug}`,
                   returnAfterRegister: `/single/${incoming.slug}`,
                   // event: incoming.event
                 };
            }
          }, [location.state]);



  // pick up eventId from prop or from navigation state
  const eventId = eventIdProp || location.state?.eventId || null;
  const fromPath = locationStateProp || location.state?.from || null;

  const [event, setEvent] = useState(null); // {_id, slug, name, currency, tickets:[{_id,label,amount_cents}]}
  const [usingFallback, setUsingFallback] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

//   // 🔐 route to login if no user
useEffect(() => {
    if (!currentUser) {
      if (redirectState) {
        // later: sessionStorage.setItem('authRedirect', JSON.stringify(redirectState));
        localStorage.setItem('IM_redirect', JSON.stringify(redirectState)); 
        navigate('/login', { replace: true, state: redirectState });
      } else {
        navigate('/login'); // normal flow
      }
    }
  }, [currentUser, redirectState, navigate]);

  // fetch event details (tickets, currency, slug)
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        if (!eventId) throw new Error('no_event_id');
        const { data } = await axios.get(`/api/events/${eventId}`);
        if (!cancel) {
          setEvent(data);
          setUsingFallback(false);
        }
      } catch {
        if (!cancel) {
          setEvent(null);
          setUsingFallback(true);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [eventId]);

  // Choose which event object drives the UI
  const effectiveEvent = usingFallback ? FALLBACK_EVENT : event || FALLBACK_EVENT;

  // Default the selected ticket to the first available
  useEffect(() => {
    if (!selectedTicket && effectiveEvent?.tickets?.length) {
      setSelectedTicket(effectiveEvent.tickets[0]);
    }
  }, [effectiveEvent, selectedTicket]);

  const appearance = useMemo(() => ({ theme: 'flat' }), []);
  // Deferred/manual mode: no clientSecret at mount.
  // amount/currency help wallet availability; not authoritative.
  const elementsOptions = useMemo(() => ({
    mode: 'payment',
    paymentMethodCreation: 'manual',
    currency: effectiveEvent.currency || 'usd',
    amount: selectedTicket?.amount_cents || 3000,
    appearance,
  }), [appearance, effectiveEvent.currency, selectedTicket?.amount_cents]);

  if (loading) {
    return (
      <Container className="py-4"><Row><Col>Loading checkout…</Col></Row></Container>
    );
  }

  return (
    <Container className="py-4">
      <Row>
        <Col md={12} className="d-flex flex-column gap-3">
          <h2>{effectiveEvent.name}</h2>

          {usingFallback && (
            <Card className="p-3">
              <div style={{ fontSize: 14, opacity: 0.8 }}>
                Showing demo tickets (backend event not found).
              </div>
            </Card>
          )}

          <Card className="p-3">
            <h5>Select a ticket</h5>
            {effectiveEvent.tickets.map(t => (
              <label key={t._id} style={{ display: 'block', margin: '6px 0' }}>
                <input
                  type="radio"
                  name="ticket"
                  checked={selectedTicket?._id === t._id}
                  onChange={() => setSelectedTicket(t)}
                />
                <span style={{ marginLeft: 8 }}>
                  {t.label} — ${(t.amount_cents / 100).toFixed(2)} {effectiveEvent.currency?.toUpperCase()}
                </span>
              </label>
            ))}
          </Card>

          <Elements
            stripe={stripePromise}
            options={elementsOptions}
            // Remount when ticket changes so wallets refresh with the new amount
            key={`${effectiveEvent.slug}-${selectedTicket?._id || 'none'}`}
          >
            <SingleCheckoutForm
              locationState={fromPath}
              eventId={eventId}                               // may be null in demo
              ticketId={selectedTicket?._id || null}
              ticketAmountCents={selectedTicket?.amount_cents || null}
              slug={effectiveEvent.slug}
              demoMode={usingFallback}                         // lets the form show friendlier errors
            />
          </Elements>
        </Col>
      </Row>
    </Container>
  );
}


export default SingleCheckout; 
